'use strict';

const libWorkspace = require('pict-section-workspace');

/**
 * LotWorkspace-Provider — the bespoke-methods subclass behind source/workspace-configs/Lot.config.js.
 *
 * The Lot's own Overview stays the declarative FieldGroups (the base handles it). Its extra surface is the "Sub Lots"
 * tab: SubLot is a child of Lot (SubLot.IDLot), and each SubLot carries its OWN per-customer metadata blob
 * (SubLotMetadata). Rather than give SubLot a standalone workspace, we NEST it here — the tab lists the lot's sub lots
 * and expands one at a time into the shared EntityMetadataForm (read-only + inline Edit/Save), reusing the same
 * guided-metadata pattern as MixDesign/Product/MixType. See [[config-app-entity-metadata]] +
 * source/providers/EntityMetadataForm-Provider.js (the EntityMetadataForm singleton hosts one record at a time, which
 * is why sub lots expand singly).
 */
class LotWorkspaceProvider extends libWorkspace.HeadlightWorkspaceBaseProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._subLots = [];
		this._subLotsLoaded = false;
		this._subLotsLoading = false;
		this._subLotsError = '';
		this._openSubLotID = 0;
	}

	get app() { return this.pict.PictApplication; }
	// Resolve-null REST (best-effort read; the tab shows its own empty/error state).
	_get(pURL) { return new Promise((resolve) => this.restClient.getJSON(pURL, (pError, pBody) => resolve(pError ? null : pBody))); }

	/** Base calls this after the record loads — reset the per-lot sub-lot state. */
	async hydrateAsync()
	{
		this._subLots = [];
		this._subLotsLoaded = false;
		this._subLotsLoading = false;
		this._subLotsError = '';
		this._openSubLotID = 0;
	}

	// --- Sub Lots tab ---------------------------------------------------------------------------------------
	_subLotsHTML()
	{
		return `<p class="lw-help">The <strong>sub lots</strong> sampled under this lot, each with its own per-customer metadata. Expand one to view or edit its metadata.</p>`
			+ `<div id="LW-SubLots" class="lw-sublots">${this._subLotsListHTML()}</div>`;
	}

	_subLotsListHTML()
	{
		if (this._subLotsError) { return `<div class="lw-error"><i class="fas fa-triangle-exclamation"></i> ${this._esc(this._subLotsError)}</div>`; }
		if (!this._subLotsLoaded) { return '<div class="lw-note"><i class="fas fa-spinner fa-spin"></i> Loading sub lots…</div>'; }
		if (!this._subLots.length) { return '<div class="lw-note">No sub lots under this lot.</div>'; }
		return this._subLots.map((pSubLot) => this._subLotRowHTML(pSubLot)).join('');
	}

	_subLotRowHTML(pSubLot)
	{
		const tmpID = Number(pSubLot.IDSubLot);
		const tmpOpen = (this._openSubLotID === tmpID);
		const tmpSummary = this._subLotSummary(pSubLot);
		return `<div class="lw-sublot${tmpOpen ? ' is-open' : ''}">`
			+ `<button type="button" class="lw-sublot-head" onclick="_Pict.providers.LotWorkspace.toggleSubLot(${tmpID})">`
			+ `<span class="lw-sublot-name"><i class="fas fa-chevron-${tmpOpen ? 'down' : 'right'}"></i> ${this._esc(pSubLot.Name || ('Sub Lot ' + tmpID))}</span>`
			+ `<span class="lw-sublot-summary">${tmpSummary}</span>`
			+ `</button>`
			+ `<div class="lw-sublot-body" id="LW-SubLotHost-${tmpID}"${tmpOpen ? '' : ' hidden'}></div>`
			+ `</div>`;
	}

	/** A couple of metadata values for the collapsed header, so the list is scannable. */
	_subLotSummary(pSubLot)
	{
		let tmpMeta = pSubLot.SubLotMetadata;
		if (tmpMeta && (typeof tmpMeta === 'string')) { try { tmpMeta = JSON.parse(tmpMeta); } catch (pE) { tmpMeta = {}; } }
		tmpMeta = tmpMeta || {};
		const tmpBits = [];
		if (tmpMeta.Plant_Tons != null) { tmpBits.push(`Plant ${tmpMeta.Plant_Tons} t`); }
		if (tmpMeta.Mainline_Tons != null) { tmpBits.push(`Mainline ${tmpMeta.Mainline_Tons} t`); }
		if (tmpMeta.Gmm != null) { tmpBits.push(`Gmm ${tmpMeta.Gmm}`); }
		return tmpBits.length ? `<span class="lw-sublot-chips">${this._esc(tmpBits.join('  ·  '))}</span>` : '';
	}

	/** Tab OnEnter — load the lot's sub lots once, then repaint the list. */
	async _ensureSubLotsAsync()
	{
		if (this._subLotsLoaded) { this._renderSubLotsList(); return; }
		if (this._subLotsLoading) { return; }
		this._subLotsLoading = true;
		this._subLotsError = '';
		this._renderSubLotsList();
		const tmpID = Number(this._id) || Number(this._record && this._record.IDLot) || 0;
		const tmpRows = await this._get(`SubLots/FilteredTo/FBV~IDLot~EQ~${tmpID}/0/500`);
		if (tmpRows === null) { this._subLotsError = 'Could not load the sub lots for this lot.'; }
		this._subLots = (Array.isArray(tmpRows) ? tmpRows : []).sort((pA, pB) =>
		{
			const tmpNA = Number(pA.Name); const tmpNB = Number(pB.Name);
			if (!isNaN(tmpNA) && !isNaN(tmpNB)) { return tmpNA - tmpNB; }
			return String(pA.Name || '').localeCompare(String(pB.Name || ''), undefined, { numeric: true });
		});
		this._subLotsLoaded = true;
		this._subLotsLoading = false;
		this._renderSubLotsList();
	}

	_renderSubLotsList() { const tmpCA = this.pict.ContentAssignment; if (tmpCA.getElement('#LW-SubLots').length) { tmpCA.assignContent('#LW-SubLots', this._subLotsListHTML()); } }

	/** Expand/collapse one sub lot; expanding opens its metadata in the shared editor (one open at a time). */
	toggleSubLot(pID)
	{
		const tmpID = Number(pID);
		this._openSubLotID = (this._openSubLotID === tmpID) ? 0 : tmpID;
		this._renderSubLotsList();
		if (this._openSubLotID !== tmpID) { return; }
		const tmpSubLot = this._subLots.find((pSubLot) => Number(pSubLot.IDSubLot) === tmpID);
		const tmpEMF = this.pict.providers.EntityMetadataForm;
		if (!tmpSubLot || !tmpEMF || (typeof tmpEMF.openWorkspaceMetadata !== 'function')) { return; }
		tmpEMF.openWorkspaceMetadata({ entityType: 'SubLot', blobColumn: 'SubLotMetadata', record: tmpSubLot, idField: 'IDSubLot', idCustomer: tmpSubLot.IDCustomer, host: `#LW-SubLotHost-${tmpID}` });
	}
}

LotWorkspaceProvider.default_configuration = { ProviderIdentifier: 'LotWorkspace' };

module.exports = LotWorkspaceProvider;
module.exports.default_configuration = LotWorkspaceProvider.default_configuration;
